import { motion } from 'framer-motion';
import { useRevealOnScroll } from '../../lib/useRevealOnScroll';
import { fadeUp, staggerContainer, staggerChild } from '../../lib/motionConfig';

/**
 * Instrument-Grade primitives — M1 foundations.
 *
 * <Surface elevation={1|2|3}> — layered card with hairline border
 * <Eyebrow>                    — mono uppercase section label
 * <Display as="h1">            — Space Grotesk display text
 * <LiveDot variant?>           — pulsing status indicator
 * <Reveal>                     — scroll-triggered entrance
 * <Stagger> + <StaggerItem>    — list stagger helpers
 */

export const Surface = ({ elevation = 1, className = '', children, as: Tag = 'div', ...rest }) => {
  const cls = {
    1: 'ig-surface',
    2: 'ig-elevated',
    3: 'ig-floating',
  }[elevation] || 'ig-surface';
  return (
    <Tag className={`${cls} ${className}`} {...rest}>
      {children}
    </Tag>
  );
};

export const Eyebrow = ({ children, className = '', ...rest }) => (
  <span className={`ig-eyebrow ${className}`} {...rest}>{children}</span>
);

export const Display = ({ as: Tag = 'h2', size = 'lg', className = '', children, ...rest }) => {
  const sizeCls = {
    xl: 'text-[clamp(40px,6vw,64px)]',
    lg: 'text-[clamp(32px,4.5vw,48px)]',
    md: 'text-[clamp(24px,3vw,28px)]',
  }[size] || '';
  return (
    <Tag className={`ig-display ${sizeCls} ${className}`} {...rest}>
      {children}
    </Tag>
  );
};

export const Numeric = ({ children, className = '', ...rest }) => (
  <span className={`ig-numeric ${className}`} {...rest}>{children}</span>
);

export const LiveDot = ({ variant = 'success', label, className = '' }) => (
  <span className={`inline-flex items-center gap-1.5 ${className}`}>
    <span className={`ig-live-dot ${variant === 'ember' ? 'ig-live-dot--ember' : ''}`} />
    {label && <span className="ig-eyebrow">{label}</span>}
  </span>
);

export const Reveal = ({ children, delay = 0, className = '', ...rest }) => {
  const [ref, revealed] = useRevealOnScroll();
  return (
    <motion.div
      ref={ref}
      initial="initial"
      animate={revealed ? 'animate' : 'initial'}
      variants={{ ...fadeUp, animate: { ...fadeUp.animate, transition: { ...fadeUp.animate.transition, delay } } }}
      className={className}
      {...rest}
    >
      {children}
    </motion.div>
  );
};

export const Stagger = ({ stagger = 0.04, className = '', children, ...rest }) => {
  const [ref, revealed] = useRevealOnScroll();
  return (
    <motion.div
      ref={ref}
      initial="initial"
      animate={revealed ? 'animate' : 'initial'}
      variants={staggerContainer(stagger)}
      className={className}
      {...rest}
    >
      {children}
    </motion.div>
  );
};

export const StaggerItem = ({ className = '', children, ...rest }) => (
  <motion.div variants={staggerChild} className={className} {...rest}>
    {children}
  </motion.div>
);

/** A simple shimmer skeleton block. */
export const Skeleton = ({ className = '', ...rest }) => (
  <div className={`ig-skeleton ${className}`} {...rest} />
);
